/**
 * The setup wizard — Step 165.
 *
 * Kai: "we need a full setup wizard." A fresh install is a seeded organisation
 * with a placeholder name, a break-glass passphrase and twenty admin pages to
 * find in the right order. This walks the first admin through them.
 *
 * ── A guide, not a second copy ─────────────────────────────────────────
 *
 * Every step drives the SAME editor or API the ordinary settings page uses:
 * the branding step embeds `AppearanceEditor`, the sign-in step opens
 * `SsoWizard`, the mail step opens `MailSetupWizard`, the team step posts to
 * `/api/admin/agents`. The wizard's own API (`/api/admin/setup`) holds only
 * what had no home — the organisation's name, the starter libraries, and the
 * completed flag. So nothing set here is "wizard state": it is the real
 * setting, editable later on the real page.
 *
 * ── Every step is skippable except the one that matters ────────────────
 *
 * A customer can come back to branding or mail. What they cannot come back
 * from is a fresh install with no admin account except break-glass, so the
 * team step is the one the Finish step refuses without.
 *
 * The step lives in the URL (`?step=`), so a refresh, a shared link or a
 * detour to a settings page lands back where you were.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Palette,
  KeyRound,
  Mail,
  Users,
  Library,
  Flag,
  Check,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  LifeBuoy,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { apiFetch, apiSend } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useUrlState } from '../lib/urlState';
import { useDirty } from '../lib/dirty';
import { useAuth } from '../lib/AuthContext';
import { useBranding, useDocumentTitle } from '../lib/useBranding';
import { cn } from '../lib/utils';
import { AppearanceEditor } from './admin/BrandingPage';
import { SsoWizard } from './admin/SsoSettingsPage';
import { MailSetupWizard } from './admin/MailSetupWizard';

interface Status {
  completed: boolean;
  completedAt: number | null;
  organisation: { name: string; slug: string | null };
  branding: {
    displayName: string | null;
    logoPath: string | null;
    faviconPath: string | null;
    brandColor: string | null;
  };
  signIn: { sso: boolean; agentLink: boolean };
  mail: { configured: boolean; active: boolean; mode: string | null; fromAddress: string | null };
  team: { admins: number; agents: number };
  libraries: {
    catalogue: { items: number; categories: string[]; installed: number; names: string[] };
    kb: {
      existingFolders: number;
      folders: {
        name: string;
        description: string | null;
        articles: number;
        titles: string[];
        installed: number;
      }[];
    };
  };
}

const STEPS = [
  { key: 'organisation', label: 'Organisation', icon: Building2 },
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'signin', label: 'Sign-in', icon: KeyRound },
  { key: 'mail', label: 'Outbound mail', icon: Mail },
  { key: 'team', label: 'Your team', icon: Users },
  { key: 'content', label: 'Starter content', icon: Library },
  { key: 'finish', label: 'Finish', icon: Flag },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

export function SetupPage() {
  useDocumentTitle('Setup');
  const branding = useBranding();
  const [stepRaw, setStep] = useUrlState('step', 'organisation');
  const step: StepKey = STEPS.some((s) => s.key === stepRaw) ? (stepRaw as StepKey) : 'organisation';
  const index = STEPS.findIndex((s) => s.key === step);
  const status = useQuery({ queryKey: qk.setupStatus, queryFn: () => apiFetch<Status>('/api/admin/setup') });

  const go = (k: StepKey) => setStep(k);
  const next = () => {
    const n = STEPS[index + 1];
    if (n) go(n.key);
  };
  const back = () => {
    const p = STEPS[index - 1];
    if (p) go(p.key);
  };

  /** What each step has to show for itself — drives the ticks in the rail. */
  const done = (k: StepKey, s: Status): boolean => {
    switch (k) {
      case 'organisation':
        return !/^4u /i.test(s.organisation.name);
      case 'branding':
        return !!(s.branding.logoPath || s.branding.brandColor);
      case 'signin':
        return s.signIn.sso || s.signIn.agentLink;
      case 'mail':
        return s.mail.configured && s.mail.active;
      case 'team':
        return s.team.admins > 0;
      case 'content':
        return s.libraries.catalogue.installed > 0 || s.libraries.kb.folders.some((f) => f.installed > 0);
      case 'finish':
        return s.completed;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex flex-wrap items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-brand/10 text-brand">
            <LifeBuoy className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">Set up {branding?.appName ?? 'your service desk'}</h1>
            <p className="text-xs text-muted-foreground">
              Seven short steps. Everything here can be changed later under Settings.
            </p>
          </div>
          {status.data?.completed && (
            <Badge variant="secondary" className="ml-auto">
              Setup completed {new Date(status.data.completedAt ?? 0).toLocaleDateString('en-GB')}
            </Badge>
          )}
        </header>

        <div className="grid gap-6 md:grid-cols-[14rem,1fr]">
          <nav
            aria-label="Setup steps"
            className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:block md:space-y-0.5 md:overflow-visible md:px-0 md:pb-0"
          >
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isDone = status.data ? done(s.key, status.data) : false;
              return (
                <button
                  key={s.key}
                  onClick={() => go(s.key)}
                  className={cn(
                    'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-sm transition-colors md:w-full md:whitespace-normal',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    step === s.key
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                      isDone ? 'bg-emerald-600 text-white' : 'border',
                    )}
                  >
                    {isDone ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <Icon className="h-4 w-4" />
                  {s.label}
                </button>
              );
            })}
          </nav>

          <section className="min-w-0 rounded-lg border bg-card">
            {status.isError && (
              <div className="p-6">
                <p className="text-sm font-medium">The setup status could not be loaded.</p>
                <Button className="mt-2" size="sm" variant="outline" onClick={() => void status.refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {status.isPending && <Skeleton className="m-6 h-64" />}
            {status.data && (
              <>
                {step === 'organisation' && <OrganisationStep status={status.data} onNext={next} />}
                {step === 'branding' && <BrandingStep onNext={next} onBack={back} />}
                {step === 'signin' && <SignInStep status={status.data} onNext={next} onBack={back} />}
                {step === 'mail' && <MailStep status={status.data} onNext={next} onBack={back} />}
                {step === 'team' && <TeamStep status={status.data} onNext={next} onBack={back} />}
                {step === 'content' && <ContentStep status={status.data} onNext={next} onBack={back} />}
                {step === 'finish' && (
                  <FinishStep
                    status={status.data}
                    done={(k) => done(k, status.data!)}
                    onBack={back}
                    go={go}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Footer({
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled,
  skip,
}: {
  onBack?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  nextLabel?: string | undefined;
  nextDisabled?: boolean | undefined;
  skip?: (() => void) | undefined;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t p-4">
      {onBack && (
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft /> Back
        </Button>
      )}
      <div className="flex-1" />
      {skip && (
        <Button variant="ghost" size="sm" onClick={skip}>
          Skip for now
        </Button>
      )}
      {onNext && (
        <Button size="sm" onClick={onNext} disabled={nextDisabled}>
          {nextLabel} <ChevronRight />
        </Button>
      )}
    </div>
  );
}

function StepHeader({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b p-6">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function useStatusRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.setupStatus });
    void qc.invalidateQueries({ queryKey: qk.branding });
  };
}

// ── 1. Organisation ──────────────────────────────────────────────────────────

function OrganisationStep({ status, onNext }: { status: Status; onNext: () => void }) {
  const refresh = useStatusRefresh();
  const [name, setName] = useState(status.organisation.name);
  const dirty = name.trim() !== status.organisation.name;
  useDirty(dirty);
  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/setup/organisation', 'PUT', { name: name.trim() }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That could not be saved.');
      refresh();
      onNext();
    },
  });
  const placeholder = /^4u /i.test(status.organisation.name);
  return (
    <>
      <StepHeader title="What is the organisation called?">
        This is the name people see on the sign-in page and in emails from the desk — the customer's name, not
        the product's.
      </StepHeader>
      <div className="space-y-4 p-6">
        {placeholder && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            The name is still the installer's placeholder. Replace it with yours.
          </p>
        )}
        <div className="max-w-md">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Organisation name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Broadcasting"
            autoFocus
          />
        </div>
        <p className="text-xs text-muted-foreground">
          A separate <em>display name</em> for the sign-in page and a product name live under Branding, next.
        </p>
      </div>
      <Footer
        onNext={() => (dirty ? save.mutate() : onNext())}
        nextLabel={dirty ? 'Save and continue' : 'Continue'}
        nextDisabled={!name.trim() || save.isPending}
      />
    </>
  );
}

// ── 2. Branding ──────────────────────────────────────────────────────────────

function BrandingStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  return (
    <>
      <StepHeader title="Make it yours">
        Logo, favicon, brand colour and the display name on the sign-in page. The same editor as Settings →
        Branding — save there, and it is saved everywhere.
      </StepHeader>
      <div className="px-6 pb-2 [&>div]:mt-0 [&>div]:border-0 [&>div]:p-0">
        <AppearanceEditor />
      </div>
      <Footer onBack={onBack} onNext={onNext} skip={onNext} />
    </>
  );
}

// ── 3. Sign-in ───────────────────────────────────────────────────────────────

function SignInStep({ status, onNext, onBack }: { status: Status; onNext: () => void; onBack: () => void }) {
  const refresh = useStatusRefresh();
  const [ssoOpen, setSsoOpen] = useState(false);
  const agentLink = useMutation({
    mutationFn: (enabled: boolean) =>
      apiSend('/api/admin/sso-settings/agent-link', 'POST', { scope: 'org', enabled }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      refresh();
    },
  });
  return (
    <>
      <StepHeader title="How will agents sign in?">
        Two doors. Most organisations on Microsoft 365 use the first; organisations without it use the second.
        Both can be on.
      </StepHeader>
      <div className="space-y-4 p-6">
        <Option
          title="Microsoft sign-in (Entra ID)"
          state={status.signIn.sso ? 'Configured' : 'Not configured'}
          ok={status.signIn.sso}
          body="Agents sign in with their work Microsoft account. The wizard creates the app registration in your tenant for you — you will need to be a Global Administrator there for the consent step."
          action={
            <Button
              size="sm"
              variant={status.signIn.sso ? 'outline' : 'default'}
              onClick={() => setSsoOpen(true)}
            >
              {status.signIn.sso ? 'Re-run Microsoft setup' : 'Set up Microsoft sign-in'}
            </Button>
          }
        />
        <Option
          title="Email sign-in links"
          state={status.signIn.agentLink ? 'On' : 'Off'}
          ok={status.signIn.agentLink}
          body="An agent types their email address and is sent a one-time link. Needs outbound mail (the next step) to actually deliver. Nothing from Microsoft required."
          action={
            <Button
              size="sm"
              variant={status.signIn.agentLink ? 'outline' : 'default'}
              disabled={agentLink.isPending}
              onClick={() => agentLink.mutate(!status.signIn.agentLink)}
            >
              {status.signIn.agentLink ? 'Turn off' : 'Turn on'}
            </Button>
          }
        />
        <p className="text-xs text-muted-foreground">
          Requesters (the people who raise tickets) always sign in with an emailed link, or with Microsoft if
          that is on. The full page is at{' '}
          <Link to="/admin/sso" className="underline">
            Settings → Sign-in
          </Link>
          .
        </p>
      </div>
      <Footer onBack={onBack} onNext={onNext} skip={onNext} />
      {ssoOpen && (
        <SsoWizard
          scope="org"
          open
          onClose={() => {
            setSsoOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function Option({
  title,
  state,
  ok,
  body,
  action,
}: {
  title: string;
  state: string;
  ok: boolean;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-md border p-4">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium">
          {title}
          <Badge
            variant={ok ? 'default' : 'secondary'}
            className={cn(ok && 'bg-emerald-600 hover:bg-emerald-600')}
          >
            {state}
          </Badge>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

// ── 4. Outbound mail ─────────────────────────────────────────────────────────

function MailStep({ status, onNext, onBack }: { status: Status; onNext: () => void; onBack: () => void }) {
  const refresh = useStatusRefresh();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [testTo, setTestTo] = useState(me?.user.email ?? '');
  const stored = useQuery({
    queryKey: qk.setupMailStored,
    queryFn: () =>
      apiFetch<{
        org: { graphClientSecret: string | null; smtpPass: string | null } | null;
        signInAppClientId?: string | null;
        credentialsReady?: boolean;
      }>('/api/admin/mail-settings'),
    enabled: open,
  });
  const test = useMutation({
    mutationFn: () => apiSend('/api/admin/mail-settings/test', 'POST', { scope: 'org', to: testTo.trim() }),
    onSuccess: (res) =>
      res.ok
        ? toast.success(`Test email sent to ${testTo.trim()}. Check the inbox — and the junk folder.`)
        : toast.error(res.error ?? 'The test failed.'),
  });
  return (
    <>
      <StepHeader title="How will the desk send email?">
        Ticket confirmations, replies, sign-in links and notifications all leave through this. Microsoft Graph
        (a shared mailbox in your tenant) or any SMTP server.
      </StepHeader>
      <div className="space-y-4 p-6">
        <Option
          title="Outbound mail"
          state={
            status.mail.configured
              ? status.mail.active
                ? `Active · ${status.mail.mode === 'smtp' ? 'SMTP' : 'Microsoft Graph'}`
                : 'Configured, not yet activated'
              : 'Not configured'
          }
          ok={status.mail.configured && status.mail.active}
          body={
            status.mail.fromAddress
              ? `Sending as ${status.mail.fromAddress}.`
              : 'Nothing can be emailed until this is set up — including sign-in links for the agents you add in the next step.'
          }
          action={
            <Button
              size="sm"
              variant={status.mail.configured ? 'outline' : 'default'}
              onClick={() => setOpen(true)}
            >
              {status.mail.configured ? 'Change' : 'Set up outbound mail'}
            </Button>
          }
        />
        {status.mail.configured && (
          <div className="rounded-md border p-4">
            <p className="text-sm font-medium">Prove it works</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Activation already needed a real test send; this lets you see one land.
            </p>
            <form
              className="mt-2 flex max-w-md gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                test.mutate();
              }}
            >
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.org"
              />
              <Button type="submit" size="sm" variant="outline" disabled={!testTo.trim() || test.isPending}>
                Send a test
              </Button>
            </form>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Inbound mail — turning emails INTO tickets — is set up afterwards under{' '}
          <Link to="/admin/mailboxes" className="underline">
            Settings → Inbound mailboxes
          </Link>
          .
        </p>
      </div>
      <Footer onBack={onBack} onNext={onNext} skip={onNext} />
      {open && stored.data && (
        <MailSetupWizard
          scope="org"
          hasStoredSecret={!!(stored.data.org?.graphClientSecret || stored.data.org?.smtpPass)}
          signInAppClientId={stored.data.signInAppClientId ?? null}
          credentialsReady={stored.data.credentialsReady ?? false}
          open
          onClose={() => {
            setOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

// ── 5. Your team ─────────────────────────────────────────────────────────────

function TeamStep({ status, onNext, onBack }: { status: Status; onNext: () => void; onBack: () => void }) {
  const refresh = useStatusRefresh();
  const qc = useQueryClient();
  const { me } = useAuth();
  const [form, setForm] = useState({ name: '', email: '' });
  const [error, setError] = useState<string | null>(null);
  useDirty(!!(form.name || form.email));
  const agents = useQuery({
    queryKey: qk.adminAgents,
    queryFn: () =>
      apiFetch<{
        entries: { id: string; name: string | null; email: string; roles: { key: string; name: string }[] }[];
        roles: { id: string; key: string; name: string }[];
      }>('/api/admin/agents'),
  });
  const orgAdminRole = agents.data?.roles.find((r) => r.key === 'org-admin');
  const admins = (agents.data?.entries ?? []).filter((a) => a.roles.some((r) => r.key === 'org-admin'));
  const create = useMutation({
    mutationFn: () =>
      apiSend<{ id: string; promoted: boolean }>('/api/admin/agents', 'POST', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        roleIds: orgAdminRole ? [orgAdminRole.id] : [],
        groupIds: [],
      }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That did not work.');
      setError(null);
      setForm({ name: '', email: '' });
      toast.success(
        status.mail.active
          ? 'Added, and emailed a sign-in link.'
          : 'Added. They can sign in with Microsoft, or with an emailed link once mail is set up.',
      );
      void qc.invalidateQueries({ queryKey: qk.adminAgents });
      refresh();
    },
  });
  const breakGlass = me?.user.id === 'breakglass';
  return (
    <>
      <StepHeader title="Who runs the desk?">
        Add at least one administrator — a real person with a real account. Agents, groups and roles come
        later under Settings → Agents and groups.
      </StepHeader>
      <div className="space-y-4 p-6">
        {breakGlass && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            You are signed in with the break-glass account. It is for emergencies, not for running a desk: add
            yourself below, then sign out and back in as that person.
          </p>
        )}
        <div className="rounded-md border">
          <div className="border-b px-4 py-2 text-xs font-medium text-muted-foreground">
            Administrators ({admins.length})
          </div>
          {agents.isPending && <Skeleton className="m-4 h-10" />}
          {admins.map((a) => (
            <div key={a.id} className="flex items-center gap-3 border-b px-4 py-2 text-sm last:border-b-0">
              <span className="font-medium">{a.name ?? a.email}</span>
              <span className="truncate text-xs text-muted-foreground">{a.email}</span>
            </div>
          ))}
          {agents.data && admins.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Nobody yet.</p>
          )}
        </div>
        <form
          className="rounded-md border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <p className="text-sm font-medium">Add an administrator</p>
          <div className="mt-2 grid max-w-xl gap-2 sm:grid-cols-2">
            <Input
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              type="email"
              placeholder="work@email.address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!form.name.trim() || !form.email.trim() || !orgAdminRole || create.isPending}
            >
              Add as administrator
            </Button>
            <span className="text-xs text-muted-foreground">
              Gets the Organisation Admin role. They will be told how to sign in.
            </span>
          </div>
        </form>
      </div>
      <Footer
        onBack={onBack}
        onNext={onNext}
        nextDisabled={admins.length === 0}
        nextLabel={admins.length === 0 ? 'Add an administrator to continue' : 'Continue'}
      />
    </>
  );
}

// ── 6. Starter content ───────────────────────────────────────────────────────

function ContentStep({ status, onNext, onBack }: { status: Status; onNext: () => void; onBack: () => void }) {
  const refresh = useStatusRefresh();
  const cat = status.libraries.catalogue;
  const kb = status.libraries.kb;
  const [catalogue, setCatalogue] = useState(cat.installed === 0);
  const [folders, setFolders] = useState<Set<string>>(
    () => new Set(kb.folders.filter((f) => f.installed === 0).map((f) => f.name)),
  );
  const nothing = !catalogue && folders.size === 0;
  const install = useMutation({
    mutationFn: () =>
      apiSend<{
        catalogue?: { itemsCreated: number; itemsSkipped: number };
        kb?: { articlesCreated: number; articlesSkipped: number };
      }>('/api/admin/setup/libraries', 'POST', {
        catalogue,
        kbFolders: [...folders],
      }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'The install failed.');
      const parts = [
        res.body?.catalogue ? `${res.body.catalogue.itemsCreated} catalogue items` : null,
        res.body?.kb ? `${res.body.kb.articlesCreated} help articles` : null,
      ].filter(Boolean);
      toast.success(
        parts.length
          ? `Installed ${parts.join(' and ')}.`
          : 'Nothing new to install — it was all there already.',
      );
      refresh();
      onNext();
    },
  });
  const toggleFolder = (name: string) =>
    setFolders((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  return (
    <>
      <StepHeader title="Start with something on the shelves">
        A working service catalogue and a library of help articles, written for a desk like this one. Tick
        what fits; untick what does not. Anything you install is yours to edit or delete, and an upgrade never
        puts it back.
      </StepHeader>
      <div className="space-y-5 p-6">
        <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-input"
            checked={catalogue}
            onChange={(e) => setCatalogue(e.target.checked)}
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
              Service catalogue library
              <Badge variant="secondary">{cat.items} items</Badge>
              {cat.installed > 0 && <Badge variant="secondary">{cat.installed} already installed</Badge>}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">
              {cat.categories.join(' · ')}. New starter, leaver, shared mailbox, email alias, distribution
              list, password reset, hardware, software, VPN, guest Wi-Fi, DNS, firewall, website change,
              travel, studio booking, and more — each with its form. Approvers and groups are yours to add.
            </span>
          </span>
        </label>

        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">Help-article library</p>
            <Badge variant="secondary">
              {kb.folders.reduce((n, f) => n + f.articles, 0)} articles in {kb.folders.length} folders
            </Badge>
            <div className="flex-1" />
            <button
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setFolders(new Set(kb.folders.map((f) => f.name)))}
            >
              All
            </button>
            <button
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setFolders(new Set())}
            >
              None
            </button>
          </div>
          <p className="mb-2 text-xs text-muted-foreground">
            Pick the folders that match what your people use. A Google shop does not need Outlook; a
            Windows-only shop does not need macOS.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {kb.folders.map((f) => (
              <label
                key={f.name}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                  folders.has(f.name) && 'border-brand/50 bg-brand/5',
                )}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input"
                  checked={folders.has(f.name)}
                  onChange={() => toggleFolder(f.name)}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    {f.name}
                    <span className="text-xs font-normal text-muted-foreground">
                      {f.articles} {f.articles === 1 ? 'article' : 'articles'}
                      {f.installed > 0 && ` · ${f.installed} installed`}
                    </span>
                  </span>
                  {f.description && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">{f.description}</span>
                  )}
                  <span
                    className="mt-1 block truncate text-[11px] text-muted-foreground/80"
                    title={f.titles.join(' · ')}
                  >
                    {f.titles.slice(0, 3).join(' · ')}
                    {f.titles.length > 3 && ` · +${f.titles.length - 3} more`}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <Footer
        onBack={onBack}
        onNext={() => (nothing ? onNext() : install.mutate())}
        nextLabel={nothing ? 'Continue without installing' : 'Install and continue'}
        nextDisabled={install.isPending}
        skip={nothing ? undefined : onNext}
      />
    </>
  );
}

// ── 7. Finish ────────────────────────────────────────────────────────────────

function FinishStep({
  status,
  done,
  onBack,
  go,
}: {
  status: Status;
  done: (k: StepKey) => boolean;
  onBack: () => void;
  go: (k: StepKey) => void;
}) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const refreshStatus = useStatusRefresh();
  const complete = useMutation({
    mutationFn: () => apiSend('/api/admin/setup/complete', 'POST', {}),
    onSuccess: async (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      await refresh();
      refreshStatus();
      toast.success('Setup complete. Welcome aboard.');
      navigate('/admin', { replace: true });
    },
  });
  const reopen = useMutation({
    mutationFn: () => apiSend('/api/admin/setup/reopen', 'POST', {}),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      refreshStatus();
      toast.success('The wizard will open for the next administrator who signs in.');
    },
  });
  const rows: { key: StepKey; label: string; detail: string }[] = [
    { key: 'organisation', label: 'Organisation', detail: status.organisation.name },
    {
      key: 'branding',
      label: 'Branding',
      detail:
        [
          status.branding.logoPath && 'logo',
          status.branding.faviconPath && 'favicon',
          status.branding.brandColor && `colour ${status.branding.brandColor}`,
        ]
          .filter(Boolean)
          .join(', ') || 'defaults',
    },
    {
      key: 'signin',
      label: 'Sign-in',
      detail:
        [status.signIn.sso && 'Microsoft', status.signIn.agentLink && 'email links']
          .filter(Boolean)
          .join(' + ') || 'none configured — only break-glass can get in',
    },
    {
      key: 'mail',
      label: 'Outbound mail',
      detail: status.mail.active
        ? `active (${status.mail.fromAddress ?? status.mail.mode})`
        : status.mail.configured
          ? 'configured, not activated'
          : 'not set up — nothing will be emailed',
    },
    {
      key: 'team',
      label: 'Your team',
      detail: `${status.team.admins} administrator${status.team.admins === 1 ? '' : 's'}, ${status.team.agents} agent${status.team.agents === 1 ? '' : 's'} in total`,
    },
    {
      key: 'content',
      label: 'Starter content',
      detail: `${status.libraries.catalogue.installed} catalogue items, ${status.libraries.kb.folders.reduce((n, f) => n + f.installed, 0)} help articles`,
    },
  ];
  const canFinish = status.team.admins > 0;
  return (
    <>
      <StepHeader title="Nearly there">
        A quick look back, then the things worth doing next — none of them are needed to open the doors.
      </StepHeader>
      <div className="space-y-5 p-6">
        <div className="divide-y rounded-md border">
          {rows.map((r) => (
            <button
              key={r.key}
              onClick={() => go(r.key)}
              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]',
                  done(r.key) ? 'bg-emerald-600 text-white' : 'border',
                )}
              >
                {done(r.key) ? <Check className="h-3 w-3" /> : '·'}
              </span>
              <span className="w-32 shrink-0 font-medium">{r.label}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.detail}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">Worth doing next</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                ['/admin/mailboxes', 'Inbound mailboxes', 'Turn emails to support@ into tickets.'],
                ['/admin/business-hours', 'Business hours', 'When the clock runs on SLAs.'],
                ['/admin/sla-policies', 'SLA policies', 'How fast is fast enough.'],
                ['/admin/directory', 'Directory sync', 'Pull requesters in from Entra.'],
                ['/admin/agents', 'Agents and groups', 'Teams, and who is in them.'],
                ['/admin/catalogue', 'Service catalogue', 'Edit the items you installed, add approvers.'],
              ] as [string, string, string][]
            ).map(([to, label, body]) => (
              <Link
                key={to}
                to={to}
                className="flex items-start gap-2 rounded-md border p-3 text-sm hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="font-medium">{label}</span>
                  <span className="block text-xs text-muted-foreground">{body}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
        {!canFinish && (
          <p className="text-xs text-destructive">
            Add at least one administrator before finishing — otherwise nobody but break-glass can sign in.
          </p>
        )}
        {status.completed && (
          <p className="text-xs text-muted-foreground">
            Setup was completed on {new Date(status.completedAt ?? 0).toLocaleString('en-GB')}. Handing this
            install to someone else?{' '}
            <button className="underline" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
              Show the wizard to the next administrator who signs in
            </button>
            .
          </p>
        )}
      </div>
      <Footer
        onBack={onBack}
        onNext={() => (status.completed ? navigate('/admin') : complete.mutate())}
        nextLabel={status.completed ? 'Back to Settings' : 'Finish setup'}
        nextDisabled={!canFinish || complete.isPending}
      />
    </>
  );
}
